import { useMutation, useQueryClient } from '@tanstack/react-query';
import toursOptionApi from '@/features/dashboard/tourOptions/api/tourOptionsApi.ts';
import { toast } from 'sonner';
import { tourOptionsApiQueryKeys } from '@/features/dashboard/tourOptions/api/tourOptionsApi.queryKeys.ts';
import { TourOptionDto } from '@/features/dashboard/tourOptions/types/TourOptionDto.ts';
import { TourOptionErrorKeys } from '@/features/dashboard/tourOptions/api/tourOptionErrors.ts';
import { isKnownBackendErrorKey } from '@/lib/api/exception.ts';
import { notifyTourOptionLimitWarnings } from '@/features/dashboard/tourOptions/hooks/tourOptionLimitWarnings.ts';
import { invalidateTourStatsQueries } from '@/utils/invalidateTourStatsQueries.ts';

type UpdateTourOptionParams = {
  optionId: string;
  dto: Omit<TourOptionDto, 'id' | 'orderIndex'>;
};

export default function useUpdateTourOption(tourId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ optionId, dto }: UpdateTourOptionParams) =>
      toursOptionApi.update(optionId, dto),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: tourOptionsApiQueryKeys.byTourId(tourId),
      });
      invalidateTourStatsQueries(queryClient, tourId);
      toast.success('Opcja wyjazdu została zapisana');
      void notifyTourOptionLimitWarnings(tourId, data);
    },
    onError: (error: Error) => {
      if (isKnownBackendErrorKey(error, TourOptionErrorKeys.VARIANT_IN_USE)) {
        toast.error('Nie można usunąć wariantu', {
          description:
            'Wariant jest używany w istniejących rezerwacjach. Zamiast usuwać, zarchiwizuj go — zniknie z oferty, ale pozostanie widoczny w rezerwacjach.',
        });
        return;
      }
      if (isKnownBackendErrorKey(error, TourOptionErrorKeys.PRICE_IN_USE)) {
        toast.error('Nie można usunąć ceny', {
          description:
            'Pozycja cennika jest używana w istniejących rezerwacjach. Zamiast usuwać, zarchiwizuj ją — zniknie z oferty, ale pozostanie widoczna w rezerwacjach.',
        });
        return;
      }
      toast.error('Wystąpił błąd', {
        description:
          error.message || 'Wystąpił błąd podczas zapisywania danych.',
      });
    },
  });
}
